import { useEffect, useState } from "react";
import { useTitanApp } from "@/hooks/useTitanApp";
import useTitanTeam from "@/hooks/useTitanTeam";
import {
  addTeam,
  updateTeam,
  deleteTeamById,
  getTeamsByCompanyId,
} from "@/dispatchers/teamDispatchers";
import { setCurrentTeams } from "@/dispatchers/appDispatchers";
import { v4 as uuidv4 } from "uuid";
import { useForm } from "react-hook-form";
import { trackFirstTimeIntercomEvent } from "@/utils/trackFirstTimeIntercomEvent";

export interface TeamFormPanelProps {
  team?: Team | null;
  isDarkMode?: boolean;
  onSave: (teamData: Team) => void;
  onBack: () => void;
}
export interface TeamsSettingsTabProps {
  teams: Team[];
  onAddTeam: () => void;
  onEditTeam: (team: Team) => void;
  onDeleteTeam: (teamId: string) => void;
  confirmDelete: (onConfirm: () => void, itemType?: string) => void;
}
export interface TeamsSettingsDataProps {
  initialTeam?: Team;
  onSave?: (teamData: Team) => void;
  onDeleteTeam?: (teamId: string) => void;
}
interface Team {
  companyID: string;
  companyIdGuid?: string;
  createdBy: string;
  createdDate: string;
  isActive: boolean;
  isDeleted: boolean;
  isGlobalView: boolean;
  isHide: boolean;
  pharmacies: Pharmacy[];
  teamDisplayName: string;
  teamID: string;
  teamIdGuid?: string;
  teamImage: string;
  teamMembers: Member[];
  teamName: string;
  updatedBy: string;
  updatedDate: string;
  teamColor: string;
  teamDescription: string;
}

interface Pharmacy {
  clinicalScore?: number;
  efficiencyScore?: number;
  financeScore?: number;
  operationsScore?: number;
  pharmacyImage?: string;
  pharmacyKPIs?: KPI[];
  teamPharmacyAddress: string;
  teamPharmacyID: string;
  teamPharmacyLatitude: string;
  teamPharmacyLongitude: string;
  teamPharmacyName: string;
  totalKPIPercentage?: number;
}
interface Member {
  companyID: string;
  email: string;
  gender: number;
  isOwner: boolean;
  roleId: string;
  teamJoiningStatus: number;
  teamMemberAccessType: number;
  joiningDate: string;
  teamMemberID: string;
  teamMemberImage: string;
  userFirstName: string;
  userFullName: string;
  userLastName: string;
}
interface KPI {
  kpiValue: string;
  kpiType: number;
}

export const useTeamsSettingsData = ({
  initialTeam,
  onSave,
  onDeleteTeam,
}: TeamsSettingsDataProps = {}) => {
  const { appState, locationState, memberState } = useTitanApp();
  const { teamState } = useTitanTeam();
  const { allPharmacies } = locationState || {};
  const { darkMode, companyId, currentUser } = appState || {
    companyId: "00000000-0000-0000-0000-000000000000",
  };
  const { membersData } = memberState || {};
  const { isTeamLoading, teams, isTeamsLoading } = teamState || {};
  const [isFormSubmitLoading, setIsFormSubmitLoading] = useState(false);

  const isDarkMode = darkMode ?? false;
  const defaultTeam: Team = {
    companyID: companyId,
    createdBy: "A91F5AD6-7E14-745F-C40F-601670410C23",
    createdDate: new Date().toISOString(),
    isActive: true,
    isDeleted: false,
    isGlobalView: false,
    isHide: false,
    pharmacies: [],
    teamDisplayName: "",
    teamID: "",
    teamImage: "",
    teamMembers: [],
    teamName: "",
    updatedBy: "A91F5AD6-7E14-745F-C40F-601670410C23",
    updatedDate: new Date().toISOString(),
    teamDescription: "",
    teamColor: "#3b82f6",
  };

  const [formData, setFormData] = useState<Team | null>(
    initialTeam || defaultTeam,
  );
  const [logoPreview, setLogoPreview] = useState<string | null>(
    formData?.teamImage || initialTeam?.teamImage || null,
  );
  const [logoImageList, setLogoImageList] = useState(
    formData?.teamImage ? [{ data_url: formData.teamImage }] : [],
  );
   const [LogoError, setLogoError] = useState<string | null>(null);

  useEffect(() => {
    if (initialTeam) {
      setFormData(initialTeam);
    }
  }, [initialTeam]);

  const capitalize = (str: string): string =>
    str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    setValue,
    watch,
    trigger,
  } = useForm({
    defaultValues: initialTeam || defaultTeam,
    mode: "onChange",
  });
  useEffect(() => {
      register("teamImage");
    register("teamMembers", {
      validate: (v: any) =>
        (Array.isArray(v) && v.length > 0) ||
        "Please select at least 1 team member",
    });
    register("pharmacies", {
      validate: (v: any) =>
        (Array.isArray(v) && v.length > 0) ||
        "Please select at least 1 team pharmacy",
    });
  }, [register]);

  // optional: when arrays change re-trigger validation (keeps errors in sync)
  useEffect(() => {
    const sub = watch((_, { name }) => {
      if (name === "teamMembers" || name === "pharmacies") {
        trigger(["teamMembers", "pharmacies"]);
      }
    });
    return () => sub.unsubscribe();
  }, [watch, trigger]);
  // display names of users in company
  const handleGetTeamMembersName = (teamMembers: Member[]): string => {
    if (!teamMembers || teamMembers?.length === 0)
      return "No team members assigned";
    return teamMembers
      .map((member) => member?.userFullName)
      .map((name) => capitalize(name!))
      .filter(Boolean)
      .join(", ");
  };
  // display names of pharmacies of company
  const handleGetPharmacyNames = (pharmacies: Pharmacy[]): string => {
    if (!pharmacies || pharmacies.length === 0) return "No pharmacies assigned";
    return pharmacies
      .map((pharmacy) => pharmacy?.teamPharmacyName)
      .map((name) => capitalize(name!))
      .filter(Boolean)
      .join(", ");
  };

  const handleLogoChange = (imageList: any[]) => {
    setLogoImageList(imageList);

    if (imageList?.length > 0) {
      const logoUrl = imageList[0]?.data_url;

      setFormData((prev) => ({
        ...prev,
        teamImage: logoUrl,
      }));

      setValue("teamImage", logoUrl, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setLogoPreview(logoUrl);
    } else {
      setFormData((prev) => ({
        ...prev,
        teamImage: "",
      }));

      setValue("teamImage", formData.teamImage, { shouldDirty: true ,shouldValidate: true});

      setLogoPreview(formData.teamImage);
    }
  };
  const handleInputChange = (field: keyof Team, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRemoveLogo = () => {
  setValue("teamImage", "", {
    shouldDirty: true,
    shouldValidate: true,
    });

  setFormData((prev) => ({
    ...prev,
    teamImage: "",
    }));

    setLogoImageList([]);
    setLogoPreview(null);
  
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const MAX_LOGO_SIZE = 1 * 1024 * 1024; 

    const file = event.target.files?.[0];
    
    if (!file) return;
    
    if (file.size > MAX_LOGO_SIZE) {
    setLogoError("Logo size must be 1 MB or less");
    event.target.value = "";
    return;
    }
  
  setLogoError(null);
   
  const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === "string") {
        // We set the logoImageList with a single item array
        handleLogoChange([{ data_url: result }]);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleToggleMemberInTeam = (emailAddress: string) => {
    setFormData((prev) => {
      const isMemberInTeam = prev.teamMembers.some(
        (m) => m.email === emailAddress,
      );

      let updatedTeamMembers;
      if (isMemberInTeam) {
        // Remove member from team.teamMembers
        updatedTeamMembers = prev.teamMembers.filter(
          (m) => !(m.email === emailAddress),
        );
        setValue("teamMembers", updatedTeamMembers, {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        // Find member in membersData and add in team.teamMembers
        const member = membersData.find((p) => p.emailAddress === emailAddress);
        if (!member) return prev;

        const mappedMember = {
          companyID: member?.companyID,
          email: member?.emailAddress,
          gender: member?.gender,
          isOwner: true,
          roleId: member?.role?._id,
          teamJoiningStatus: 0,
          joiningDate: new Date().toISOString(),
          teamMemberAccessType: 0,
          teamMemberID: member?.userId,
          teamMemberImage: member?.userImage,
          userFirstName: member?.firstName,
          userFullName: member?.fullName,
          userLastName: member?.lastName,
        };
        updatedTeamMembers = [...prev.teamMembers, mappedMember];
        setValue("teamMembers", updatedTeamMembers, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      setValue("teamMembers", updatedTeamMembers);
      return {
        ...prev,
        teamMembers: updatedTeamMembers,
      };
    });
  };
  const handleTogglePharmacyInTeam = (pharmacyID: string) => {
    setFormData((prev) => {
      const isPharmacyInTeam = prev.pharmacies.some(
        (p) => p.teamPharmacyID === pharmacyID,
      );

      let updatedPharmacies;
      if (isPharmacyInTeam) {
        // Remove pharmacy from formData
        updatedPharmacies = prev.pharmacies.filter(
          (p) => p.teamPharmacyID !== pharmacyID,
        );
        setValue("pharmacies", updatedPharmacies, {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        // Find full pharmacy object from allPharmacies
        const pharmacy = allPharmacies.find((p) => p.pharmacyID === pharmacyID);
        if (!pharmacy) return prev;
        // map it into team pharmacy format
        const mappedPharmacy = {
          teamPharmacyID: pharmacy.pharmacyID,
          teamPharmacyName: pharmacy.pharmacyName,
          teamPharmacyAddress: pharmacy.pharmacyAddress,
          teamPharmacyLongitude: pharmacy.longitude.toString(),
          teamPharmacyLatitude: pharmacy.latitude.toString(),
          clinicalKPI: pharmacy.clinicalKPI,
          financeKPI: pharmacy.financeKPI,
          operationsKPI: pharmacy.operationsKPI,
          efficiencyKPI: pharmacy.efficiencyKPI,
          pharmacyKPIs: pharmacy.pharmacyKPIs,
          pharmacyImage: pharmacy.pharmacyImage,
          isHelpRequired: pharmacy.isHelpRequired,
          patients: pharmacy.patients,
        };
        updatedPharmacies = [...prev.pharmacies, mappedPharmacy];

        setValue("pharmacies", updatedPharmacies, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      // Update react-hook-form value for validation
      setValue("pharmacies", updatedPharmacies, { shouldValidate: true });

      return {
        ...prev,
        pharmacies: updatedPharmacies,
      };
    });
  };
  const handleDeleteTeamSettings = async (teamId) => {
    try {
      await deleteTeamById(teamId);
      const teams = await getTeamsByCompanyId(companyId);
      await setCurrentTeams(teams);
    } catch (err) {
      console.error("Failed to delete team", err);
    }
  };

  const handleSaveTeamSettings = async (teamData: any) => {
    try {
      if (formData.teamID && formData.teamID.trim()) {
        await updateTeam(teamData);
        // const updatedTeams = await getTeamsByCompanyId(companyId);
        // await setCurrentTeams(updatedTeams);
        return;
      }
      const newId = uuidv4();
      const payload: Team = {
        ...teamData,
        teamID: newId,
        teamDisplayName: teamData.teamName || "",
      };

      setFormData((prev) => ({
        ...prev,
        teamID: newId,
        teamDisplayName: payload.teamDisplayName,
      }));
      await addTeam(payload);
      trackFirstTimeIntercomEvent({
        eventName: "team_created_first_time",
        user: {
          id: currentUser.emailAddress,
          email: currentUser.emailAddress,
          name: currentUser.fullName,
        },
        properties: {
          source: "settings_teams",
        },
      });
    } catch (err) {
      console.error("Failed to save team", err);
    }
  };
  const onSubmit = async (teamData: any) => {
    
    if (!isValid || !isDirty) {
      return;
    }
    setIsFormSubmitLoading(true);
    await handleSaveTeamSettings(teamData);
    const updatedTeams = await getTeamsByCompanyId(companyId);
    await setCurrentTeams(updatedTeams);
    setIsFormSubmitLoading(false);
    onSave(teamData);
  };
  const handleDeleteTeamButton = async (teamId) => {
    await handleDeleteTeamSettings(teamId);
  };

  return {
    isDarkMode,
    teams,
    allPharmacies,
    formData,
    membersData,
    logoPreview,
    isTeamLoading,
    errors,
    isValid,
    isDirty,
    control,
    isTeamsLoading,
    register,
    watch,
    setValue,
    setFormData,
    setLogoPreview,
    handleGetTeamMembersName,
    handleGetPharmacyNames,
    handleTogglePharmacyInTeam,
    handleToggleMemberInTeam,
    handleLogoUpload,
    onSubmit,
    handleDeleteTeamButton,
    handleSubmit,
    handleInputChange,
    isFormSubmitLoading,
    LogoError,
    handleRemoveLogo
  };
};
